import {
  GetHoldingUnitBusinessActivityRequestSchema,
  GetHoldingUnitBusinessActivityResponseSchema,
  GetHoldingUnitLoadSessionDetailRequestSchema,
  GetHoldingUnitLoadSessionDetailResponseSchema,
  GetHoldingUnitReservationStateRequestSchema,
  GetHoldingUnitReservationStateResponseSchema,
  GetInventoryByYardRequestSchema,
  GetInventoryByYardResponseSchema,
  GetHoldingUnitTechnicalAuditRequestSchema,
  GetHoldingUnitTechnicalAuditResponseSchema,
  ListHoldingUnitLoadSessionsRequestSchema,
  ListHoldingUnitLoadSessionsResponseSchema,
  ListHoldingUnitsRequestSchema,
  ListHoldingUnitsResponseSchema,
  type GetHoldingUnitLoadSessionDetailRequest,
  type GetHoldingUnitLoadSessionDetailResponse,
  type GetHoldingUnitReservationStateRequest,
  type GetHoldingUnitReservationStateResponse,
  type GetHoldingUnitBusinessActivityRequest,
  type GetHoldingUnitBusinessActivityResponse,
  type GetInventoryByYardRequest,
  type GetInventoryByYardResponse,
  type GetHoldingUnitTechnicalAuditRequest,
  type GetHoldingUnitTechnicalAuditResponse,
  type ListHoldingUnitLoadSessionsRequest,
  type ListHoldingUnitLoadSessionsResponse,
  type ListHoldingUnitsRequest,
  type ListHoldingUnitsResponse,
} from '@crv/contracts/holding-units';

import { apiGet } from '../http-client';

export interface ListHoldingUnitsFilters extends ListHoldingUnitsRequest {
  yardLocationId?: string | null;
  page?: number;
  pageSize?: number;
}

export async function getInventoryByYard(
  request: GetInventoryByYardRequest,
): Promise<GetInventoryByYardResponse> {
  const params = GetInventoryByYardRequestSchema.parse(request);
  const searchParams = new URLSearchParams();
  if (params.storeId) {
    searchParams.set('storeId', params.storeId);
  }
  if (params.locationId) {
    searchParams.set('locationId', params.locationId);
  }
  if (params.includeInactiveYards !== undefined) {
    searchParams.set('includeInactiveYards', String(params.includeInactiveYards));
  }
  if (params.lifecycleStatuses && params.lifecycleStatuses.length > 0) {
    searchParams.set('lifecycleStatuses', params.lifecycleStatuses.join(','));
  }
  if (params.materialId) {
    searchParams.set('materialId', params.materialId);
  }
  if (params.groupByMaterial !== undefined) {
    searchParams.set('groupByMaterial', String(params.groupByMaterial));
  }

  const query = searchParams.toString();
  const path =
    query.length > 0
      ? `/v1/companies/${params.companyId}/holding-units/reports/inventory-by-yard?${query}`
      : `/v1/companies/${params.companyId}/holding-units/reports/inventory-by-yard`;
  return apiGet(path, GetInventoryByYardResponseSchema);
}

export async function listHoldingUnits(
  request: ListHoldingUnitsFilters,
): Promise<ListHoldingUnitsResponse> {
  const params = ListHoldingUnitsRequestSchema.parse(request);
  const searchParams = new URLSearchParams();
  if (params.storeId) {
    searchParams.set('storeId', params.storeId);
  }
  if (params.locationId) {
    searchParams.set('locationId', params.locationId);
  }
  if (params.lifecycleStatus) {
    searchParams.set('lifecycleStatus', params.lifecycleStatus);
  }
  if (params.includeSuperseded !== undefined) {
    searchParams.set('includeSuperseded', String(params.includeSuperseded));
  }
  if (params.materialId) {
    searchParams.set('materialId', params.materialId);
  }
  if (params.unitProfileId) {
    searchParams.set('unitProfileId', params.unitProfileId);
  }
  if (params.mode) {
    searchParams.set('mode', params.mode);
  }
  if (params.statusMode) {
    searchParams.set('statusMode', params.statusMode);
  }
  if (params.limit) {
    searchParams.set('limit', String(params.limit));
  }
  if (request.yardLocationId !== undefined) {
    searchParams.set('yardLocationId', request.yardLocationId ?? 'unassigned');
  }
  if (request.page) {
    searchParams.set('page', String(request.page));
  }
  if (request.pageSize) {
    searchParams.set('pageSize', String(request.pageSize));
  }

  const query = searchParams.toString();
  const path =
    query.length > 0
      ? `/v1/companies/${params.companyId}/holding-units?${query}`
      : `/v1/companies/${params.companyId}/holding-units`;
  return apiGet(path, ListHoldingUnitsResponseSchema);
}

export async function getHoldingUnitBusinessActivity(
  request: GetHoldingUnitBusinessActivityRequest,
): Promise<GetHoldingUnitBusinessActivityResponse> {
  const params = GetHoldingUnitBusinessActivityRequestSchema.parse(request);
  return apiGet(
    `/v1/companies/${params.companyId}/holding-units/${params.unitId}/business-activity`,
    GetHoldingUnitBusinessActivityResponseSchema,
  );
}

export async function getHoldingUnitTechnicalAudit(
  request: GetHoldingUnitTechnicalAuditRequest,
): Promise<GetHoldingUnitTechnicalAuditResponse> {
  const params = GetHoldingUnitTechnicalAuditRequestSchema.parse(request);
  const searchParams = new URLSearchParams();
  if (params.from) {
    searchParams.set('from', params.from);
  }
  if (params.to) {
    searchParams.set('to', params.to);
  }
  if (params.limit) {
    searchParams.set('limit', String(params.limit));
  }
  const query = searchParams.toString();
  const path = `/v1/companies/${params.companyId}/holding-units/${params.unitId}/technical-audit`;
  return apiGet(
    query.length > 0 ? `${path}?${query}` : path,
    GetHoldingUnitTechnicalAuditResponseSchema,
  );
}

export async function getHoldingUnitReservationState(
  request: GetHoldingUnitReservationStateRequest,
): Promise<GetHoldingUnitReservationStateResponse> {
  const params = GetHoldingUnitReservationStateRequestSchema.parse(request);
  return apiGet(
    `/v1/companies/${params.companyId}/holding-units/${params.unitId}/reservations`,
    GetHoldingUnitReservationStateResponseSchema,
  );
}

export async function listHoldingUnitLoadSessions(
  request: ListHoldingUnitLoadSessionsRequest,
): Promise<ListHoldingUnitLoadSessionsResponse> {
  const params = ListHoldingUnitLoadSessionsRequestSchema.parse(request);
  const searchParams = new URLSearchParams();
  if (params.storeId) {
    searchParams.set('storeId', params.storeId);
  }
  if (params.locationId) {
    searchParams.set('locationId', params.locationId);
  }
  if (params.status) {
    searchParams.set('status', params.status);
  }
  if (params.from) {
    searchParams.set('from', params.from);
  }
  if (params.to) {
    searchParams.set('to', params.to);
  }

  const query = searchParams.toString();
  const path =
    query.length > 0
      ? `/v1/companies/${params.companyId}/holding-units/load-sessions?${query}`
      : `/v1/companies/${params.companyId}/holding-units/load-sessions`;
  return apiGet(path, ListHoldingUnitLoadSessionsResponseSchema);
}

export async function getHoldingUnitLoadSessionDetail(
  request: GetHoldingUnitLoadSessionDetailRequest,
): Promise<GetHoldingUnitLoadSessionDetailResponse> {
  const params = GetHoldingUnitLoadSessionDetailRequestSchema.parse(request);
  return apiGet(
    `/v1/companies/${params.companyId}/holding-units/load-sessions/${params.loadSessionId}/detail`,
    GetHoldingUnitLoadSessionDetailResponseSchema,
  );
}
